import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStatus, useData, isNormal } from 'resift';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import useInterval from 'helpers/useInterval';

// Stores / Actions
import makeGlobalConfigFetch from 'store/configuration/actions/globalConfigFetch';
import makeEntityTypesFetch from 'store/configuration/actions/entityTypesFetch';
import getOnboardingTasks from 'fetches/getOnboardingTasks';
import { setOnboardingTasks, setOverallCompletion, State } from 'store/onboarding';
import { fetchClient } from '@sift/resift/redux/reducers/client';
import { fetchSiftBot } from 'store/siftBot';

const second = 2 * 1000;
const minute = 60 * second;
const twoMinutes = 2 * minute;

interface Props {
  siftBotStatus: any;
  clientStatus: any;
  children: (shouldLoadApp: boolean) => any;
}

function AppFetcher(props: Props) {
  const globalConfigFetch = makeGlobalConfigFetch();
  const entityTypesFetch = makeEntityTypesFetch();

  const { children, siftBotStatus, clientStatus } = props;
  const dispatch = useDispatch();
  const entityTypesStatus = useStatus(entityTypesFetch);
  const globalConfigStatus = useStatus(globalConfigFetch);
  const onboardingTasksStatus = useStatus(getOnboardingTasks);
  const onboardingTasksFromAPI = useData(getOnboardingTasks);

  const onboardingState: State = useSelector((state: any) => state.onboarding);
  const currentCheckList = onboardingState.checkList;

  useEffect(() => {
    dispatch(fetchClient());
    dispatch(entityTypesFetch());
    dispatch(globalConfigFetch());
    dispatch(fetchSiftBot());
    dispatch(getOnboardingTasks());
  }, []);

  // (pearl) Update open, completion status, and slide key in real time
  // also need to manage when completed category and steps would go to the end of the list

  useInterval(() => {
    // (pearl) note: Polling every two minutes to get automatically mark completion tasks
    dispatch(getOnboardingTasks());
  }, twoMinutes);

  useEffect(() => {
    if (onboardingTasksFromAPI) {
      if (_isEmpty(currentCheckList)) {
        if (Array.isArray(onboardingTasksFromAPI)) {
          dispatch(setOnboardingTasks(onboardingTasksFromAPI));
        } else {
          const { completionPercentage, taskCategories } = onboardingTasksFromAPI;

          dispatch(setOnboardingTasks(taskCategories));
          dispatch(setOverallCompletion(completionPercentage));
        }
      } else {
        const onboardingTasksArray = Array.isArray(onboardingTasksFromAPI)
          ? onboardingTasksFromAPI
          : onboardingTasksFromAPI.taskCategories;

        const categoryLookup = _keyBy(onboardingTasksArray, 'id');
        const updatedTasks = currentCheckList.map(category => {
          const taskLookup = _keyBy(categoryLookup[category.id].tasks, 'id');

          return {
            ...category,
            tasks: category.tasks.map(t => ({
              ...t,
              isComplete: taskLookup[t.id].isComplete,
            })),
            completionPercentage: categoryLookup[category.id].completionPercentage,
          };
        });

        dispatch(setOnboardingTasks(updatedTasks));
      }
    }
  }, [onboardingTasksFromAPI]);

  const allFetchesAreComplete =
    siftBotStatus.isNormal() &&
    clientStatus.isNormal() &&
    isNormal(entityTypesStatus) &&
    isNormal(globalConfigStatus) &&
    isNormal(onboardingTasksStatus);

  return children(allFetchesAreComplete);
}

export default AppFetcher;
